import React, { createContext, useContext, useState, ReactNode } from 'react';

interface Journalist {
  id: string;
  first_name?: string;
  last_name?: string;
  organization?: string;
  title?: string;
  email?: string;
  handle?: string;
  sector?: string;
  language?: string;
  linkedin?: string;
  location?: string;
  phone?: string;
  brand_id: string;
  user_id?: string;
  created_at: string;
}

interface JournalistDialogContextType {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  editingJournalist: Journalist | null;
  setEditingJournalist: (journalist: Journalist | null) => void;
  openDialog: (journalist?: Journalist) => void;
  closeDialog: () => void;
  onSuccess?: () => void;
  setOnSuccess: (callback?: () => void) => void;
}

const JournalistDialogContext = createContext<JournalistDialogContextType | undefined>(undefined);

export const JournalistDialogProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [editingJournalist, setEditingJournalist] = useState<Journalist | null>(null);
  const [onSuccess, setOnSuccess] = useState<(() => void) | undefined>();

  const openDialog = (journalist?: Journalist) => {
    if (journalist) {
      setEditingJournalist(journalist);
    } else {
      setEditingJournalist(null);
    }
    setIsOpen(true);
  };

  const closeDialog = () => {
    setIsOpen(false);
    setTimeout(() => {
      setEditingJournalist(null);
      setOnSuccess(undefined);
    }, 300);
  };

  return (
    <JournalistDialogContext.Provider
      value={{
        isOpen,
        setIsOpen,
        editingJournalist,
        setEditingJournalist,
        openDialog,
        closeDialog,
        onSuccess,
        setOnSuccess,
      }}
    >
      {children}
    </JournalistDialogContext.Provider>
  );
};

export const useJournalistDialog = () => {
  const context = useContext(JournalistDialogContext);
  if (!context) {
    throw new Error('useJournalistDialog must be used within JournalistDialogProvider');
  }
  return context;
};
